/**
 * Utilitaires de formatage pour l'affichage
 */

import { CURRENCY_SYMBOL } from '../constants/denominations';

/**
 * Formate un montant en FCFA avec séparateurs de milliers
 *
 * @example
 * formatMoney(1500000) // "1 500 000 FCFA"
 * formatMoney(1500000, false) // "1 500 000"
 */
export function formatMoney(amount: number, includeCurrency = true): string {
  const formatted = new Intl.NumberFormat('fr-FR', {
    style: 'decimal',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);

  return includeCurrency ? `${formatted} ${CURRENCY_SYMBOL}` : formatted;
}

/**
 * Formate une date au format français court
 *
 * @example
 * formatDate(new Date('2026-01-23')) // "23/01/2026"
 */
export function formatDate(date: Date): string {
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
}

/**
 * Formate une date au format français long
 *
 * @example
 * formatDateLong(new Date('2026-01-23')) // "23 janvier 2026"
 */
export function formatDateLong(date: Date): string {
  return new Intl.DateTimeFormat('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date);
}

/**
 * Formate une heure
 *
 * @example
 * formatTime(new Date('2026-01-23T14:30:00')) // "14:30"
 */
export function formatTime(date: Date): string {
  return new Intl.DateTimeFormat('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

/**
 * Formate une date avec l'heure
 *
 * @example
 * formatDateTime(new Date('2026-01-23T14:30:00')) // "23/01/2026 à 14:30"
 */
export function formatDateTime(date: Date): string {
  return `${formatDate(date)} à ${formatTime(date)}`;
}

/**
 * Formate un numéro de compte avec séparateurs
 *
 * @example
 * formatAccountNumber('001234567890') // "0012 3456 7890"
 */
export function formatAccountNumber(accountNumber: string): string {
  if (accountNumber.length < 10) {
    return accountNumber;
  }

  // Grouper par blocs de 4 chiffres (supporte 12, 17 ou autres longueurs)
  return accountNumber.replace(/(.{4})/g, '$1 ').trim();
}

/**
 * Formate un numéro de téléphone ivoirien
 *
 * @example
 * formatPhoneNumber('0708090102') // "07 08 09 01 02"
 */
export function formatPhoneNumber(phone: string): string {
  const cleaned = phone.replace(/\D/g, '');

  if (cleaned.length === 10) {
    return cleaned.replace(/(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})/, '$1 $2 $3 $4 $5');
  }

  return phone;
}

/**
 * Tronque un texte avec ellipse
 *
 * @example
 * truncate('Un texte très long', 10) // "Un texte..."
 */
export function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) {
    return text;
  }
  return text.slice(0, maxLength - 3) + '...';
}

/**
 * Capitalise la première lettre d'une chaîne
 *
 * @example
 * capitalize('amadou diallo') // "Amadou diallo"
 */
export function capitalize(text: string): string {
  if (!text) return text;
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
}

/**
 * Capitalise chaque mot
 *
 * @example
 * capitalizeWords('amadou diallo') // "Amadou Diallo"
 */
export function capitalizeWords(text: string): string {
  return text
    .split(' ')
    .map(word => capitalize(word))
    .join(' ');
}
